import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  CurrencyCode,
  CURRENCIES,
  FALLBACK_RATES,
  SUPPORTED_CURRENCY_CODES,
  detectCurrencyFromBrowser,
  formatCurrency,
} from "@/lib/currency";

interface CurrencyContextValue {
  currency: CurrencyCode;
  setCurrency: (c: CurrencyCode) => void;
  rate: number; // INR -> currency
  rates: Record<CurrencyCode, number>;
  formatPrice: (amountInInr: number, opts?: { maximumFractionDigits?: number }) => string;
  isLoadingRates: boolean;
}

const CurrencyContext = createContext<CurrencyContextValue | undefined>(undefined);

const STORAGE_KEY = "gaonova_currency";

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [currency, setCurrencyState] = useState<CurrencyCode>(() => {
    if (typeof window === "undefined") return "INR";
    const stored = localStorage.getItem(STORAGE_KEY) as CurrencyCode | null;
    if (stored && SUPPORTED_CURRENCY_CODES.includes(stored)) return stored;
    return detectCurrencyFromBrowser();
  });

  const [rates, setRates] = useState<Record<CurrencyCode, number>>(FALLBACK_RATES);
  const [isLoadingRates, setIsLoadingRates] = useState(true);

  // Load rates from DB on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from("exchange_rates")
          .select("target_currency, rate")
          .eq("base_currency", "INR");
        if (cancelled) return;
        if (!error && data && data.length > 0) {
          const next = { ...FALLBACK_RATES };
          for (const row of data) {
            const code = row.target_currency as CurrencyCode;
            if (SUPPORTED_CURRENCY_CODES.includes(code)) {
              next[code] = Number(row.rate);
            }
          }
          setRates(next);
        }
      } catch {
        // use fallback
      } finally {
        if (!cancelled) setIsLoadingRates(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setCurrency = (c: CurrencyCode) => {
    setCurrencyState(c);
    try {
      localStorage.setItem(STORAGE_KEY, c);
    } catch {
      // ignore
    }
  };

  const rate = rates[currency] ?? 1;

  const formatPrice = useMemo(
    () =>
      (amountInInr: number, opts?: { maximumFractionDigits?: number }) =>
        formatCurrency(amountInInr, currency, rate, opts),
    [currency, rate]
  );

  const value: CurrencyContextValue = {
    currency,
    setCurrency,
    rate,
    rates,
    formatPrice,
    isLoadingRates,
  };

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be used within CurrencyProvider");
  return ctx;
}

// Re-export for convenience
export { CURRENCIES };
export type { CurrencyCode };
